import { LongPressTips } from "./components/LongPressTips";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { IS_GRIP_SITE, gp, stripGripPrefix } from "./lib/site";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./features/auth";

// Public pages
import { HomePage } from "./pages/HomePage";
import { PricingPage } from "./pages/PricingPage";
import { MePage } from "./pages/MePage";
import { SeasonPage } from "./pages/SeasonPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";

// Auth pages
import { LoginPage } from "./pages/LoginPage";
import { SignUpPage } from "./pages/SignUpPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";

// Protected pages
import { OnboardingPage } from "./pages/OnboardingPage";
import { DashboardPage } from "./pages/DashboardPage";
import { RaceListPage } from "./pages/RaceListPage";
import { RaceDetailPage, RaceDetailRedirect } from "./pages/RaceDetailPage";
import { LiveListPage, LiveRacePage, UpcomingRacePage } from "./pages/LiveRacePage";
import { isReplaySlug } from "./features/live/liveKey";
import { QualifyingListPage } from "./pages/QualifyingListPage";
import { QualifyingDetailPage } from "./pages/QualifyingDetailPage";
import { NotFoundPage } from "./pages/NotFoundPage";

// Settings pages
import { SettingsLayout } from "./pages/settings/SettingsLayout";
import { AccountSettingsPage } from "./pages/settings/AccountSettingsPage";
import { PreferencesPage } from "./pages/settings/PreferencesPage";
import { MyCarsPage } from "./pages/settings/MyCarsPage";
import { MyDriversPage } from "./pages/settings/MyDriversPage";
import { AlertsOffPage } from "./pages/AlertsOffPage";
import { BillingSettingsPage } from "./pages/settings/BillingSettingsPage";

// Admin pages
import { AdminLayout } from "./features/admin/AdminLayout";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminRacesPage } from "./pages/admin/AdminRacesPage";
import { AdminUploadPage } from "./pages/admin/AdminUploadPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminAuditLogPage } from "./pages/admin/AdminAuditLogPage";
import { AdminQualifyingUploadPage } from "./pages/admin/AdminQualifyingUploadPage";
import { AdminApiTokensPage } from "./pages/admin/AdminApiTokensPage";
import { AdminAnalysisPage } from "./pages/admin/AdminAnalysisPage";
import { AdminEngagementPage } from "./pages/admin/AdminEngagementPage";

// Finding Grip (tire pressure & damper tools) — its own shell, and its own site
import { GripAppLayout, GripPublicLayout } from "./features/grip/GripLayout";
import { GripHomePage, GripPricingPage } from "./pages/grip/GripPublicPages";
import { GripDashboardPage } from "./pages/grip/GripDashboardPage";
import { GripSessionsPage } from "./pages/grip/GripSessionsPage";
import { GripSessionFormPage } from "./pages/grip/GripSessionFormPage";
import { GripCalculatorPage } from "./pages/grip/GripCalculatorPage";
import { GripOutcomePage } from "./pages/grip/GripOutcomePage";
import { GripDamperPage } from "./pages/grip/GripDamperPage";
import { GripGuidePage } from "./pages/grip/GripGuidePage";
import { GripSettingsPage } from "./pages/grip/GripSettingsPage";
import { GripAdminPage } from "./pages/grip/GripAdminPage";
import { SupportThanksPage } from "./features/support/SupportThanksPage";

/** Finding Grip pages. At /grip/... on RaceTrace; at the root of the Finding Grip site. */
function gripRoutes() {
  return (
    <>
      {/* Public pages */}
      <Route element={<GripPublicLayout />}>
        <Route path={gp("")} element={<GripHomePage />} />
        <Route path={gp("/pricing")} element={<GripPricingPage />} />
        {/* Stripe returns here after a coffee for Finding Grip */}
        <Route path={gp("/support/thanks")} element={<SupportThanksPage />} />
      </Route>

      {/* Signed-in app (login and access are checked in the layout) */}
      <Route element={<GripAppLayout />}>
        <Route path={gp("/dashboard")} element={<GripDashboardPage />} />
        <Route path={gp("/sessions")} element={<GripSessionsPage />} />
        <Route path={gp("/sessions/new")} element={<GripSessionFormPage />} />
        <Route path={gp("/sessions/:id/edit")} element={<GripSessionFormPage />} />
        <Route path={gp("/calculate")} element={<GripCalculatorPage />} />
        <Route path={gp("/calculations/:id/outcome")} element={<GripOutcomePage />} />
        {/* Older links: converting is now part of recording how the run came in. */}
        <Route path={gp("/calculations/:id/convert")} element={<GripOutcomePage />} />
        <Route path={gp("/damper")} element={<GripDamperPage />} />
        <Route path={gp("/guide")} element={<GripGuidePage />} />
        <Route path={gp("/settings")} element={<GripSettingsPage />} />
        <Route
          path={gp("/admin")}
          element={
            <ProtectedRoute requireAdmin>
              <GripAdminPage />
            </ProtectedRoute>
          }
        />
      </Route>
    </>
  );
}

/** Old /grip/... links opened on the Finding Grip site go to the same page without the prefix. */
function StripGripPrefix() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: stripGripPrefix(location.pathname), search: location.search }}
      replace
    />
  );
}

/**
 * The Finding Grip site: only Finding Grip, with the shared account pages
 * (log in, sign up, password reset, terms) shown in its own shell.
 */
function GripSite() {
  return (
    <>
    {/* Phones: press and hold a control for its hover tip. */}
    <LongPressTips />
    <Routes>
      {gripRoutes()}

      <Route element={<GripPublicLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* RaceTrace's welcome tour does not apply here. */}
        <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
        <Route path="/grip/*" element={<StripGripPrefix />} />
        <Route path="/grip" element={<StripGripPrefix />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route element={<GripAppLayout />}>
        <Route
          path="/account"
          element={
            <div className="container-page max-w-3xl">
              <AccountSettingsPage />
            </div>
          }
        />
      </Route>
    </Routes>
    </>
  );
}

/** /live/:slug: signed in, except a replay. */
function LiveRaceRoute() {
  const { slug } = useParams<{ slug: string }>();
  return isReplaySlug(slug) ? <LiveRacePage /> : <ProtectedRoute><LiveRacePage /></ProtectedRoute>;
}

export function App() {
  if (IS_GRIP_SITE) return <GripSite />;

  return (
    <>
    {/* Phones: press and hold a control for its hover tip. */}
    <LongPressTips />
    <Routes>
      {gripRoutes()}

      <Route element={<Layout />}>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/me" element={<MePage />} />
        <Route path="/support/thanks" element={<SupportThanksPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Onboarding (protected) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Dashboard (protected) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route path="/season" element={<ProtectedRoute><SeasonPage /></ProtectedRoute>} />

        {/* Race routes: signed in only (signed out, the home page shows the latest and next race). The alerts-off link from an email works signed out. */}
        <Route path="/races" element={<ProtectedRoute><RaceListPage /></ProtectedRoute>} />
        <Route path="/alerts/off" element={<AlertsOffPage />} />
        <Route path="/chart" element={<ProtectedRoute><RaceDetailPage /></ProtectedRoute>} />
        {/* RaceTrace Live: WRL sessions still running, by RaceTrace slug (signed in) */}
        <Route path="/live" element={<ProtectedRoute><LiveListPage /></ProtectedRoute>} />
        <Route path="/live/next/:id" element={<ProtectedRoute><UpcomingRacePage /></ProtectedRoute>} />
        {/* A replay of a finished race (/live/replay-…) is open to everyone: a link to show RaceTrace Live. */}
        <Route path="/live/:slug" element={<LiveRaceRoute />} />
        {/* Redirect old bookmarked /races/:id URLs to /chart?race=:id */}
        <Route path="/races/:id" element={<ProtectedRoute><RaceDetailRedirect /></ProtectedRoute>} />

        {/* Qualifying routes (signed in) */}
        <Route path="/qualifying" element={<ProtectedRoute><QualifyingListPage /></ProtectedRoute>} />
        <Route path="/qualifying/:id" element={<ProtectedRoute><QualifyingDetailPage /></ProtectedRoute>} />

        {/* Settings (protected, nested layout) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="account" replace />} />
          <Route path="account" element={<AccountSettingsPage />} />
          <Route path="preferences" element={<PreferencesPage />} />
          <Route path="cars" element={<MyCarsPage />} />
          <Route path="drivers" element={<MyDriversPage />} />
          <Route path="billing" element={<BillingSettingsPage />} />
        </Route>

        {/* Admin routes (protected, admin only, nested layout) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="races" element={<AdminRacesPage />} />
          <Route path="analysis" element={<AdminAnalysisPage />} />
          <Route path="engagement" element={<AdminEngagementPage />} />
          <Route path="races/new" element={<AdminUploadPage />} />
          <Route path="qualifying/new" element={<AdminQualifyingUploadPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="audit-log" element={<AdminAuditLogPage />} />
          <Route path="api-tokens" element={<AdminApiTokensPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </>
  );
}
